import { NavLink } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { BuildingFilled } from 'src/shared/components/icons/building-filled'
import useGetEnterprise from 'src/shared/features/enterprise/api/use-get-enterprise'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { EnterpriseCompanyTypeValues } from 'src/shared/schemas/forms/auth'
import { enterpriseCompanyTypeMap } from 'src/shared/schemas/forms/auth'
import { cn } from 'src/shared/utils/cn'

type SidebarRouteItem = {
  label: string
  to: string
  external?: boolean
}

type SidebarSection = {
  title: string
  items: SidebarRouteItem[]
}

const settingsSections: SidebarSection[] = [
  {
    title: 'Account',
    items: [
      { label: 'Personal info', to: paths.enterprise.dashboard.settings.personalInfo.getHref() },
      // { label: 'Company info', to: paths.enterprise.dashboard.settings.companyInfo.getHref() },
      // { label: 'Members', to: paths.enterprise.dashboard.settings.members.getHref() },
    ],
  },
  {
    title: 'Preferences',
    items: [
      // { label: 'Integrations', to: paths.enterprise.dashboard.settings.integrations.getHref() },
      { label: 'Workflows', to: paths.enterprise.dashboard.settings.workflows.getHref() },
      { label: 'Lead distribution', to: paths.enterprise.dashboard.settings.leadDistribution.getHref() },
    ],
  },
  {
    title: 'Dev tools',
    items: [
      { label: 'API', to: paths.enterprise.dashboard.settings.api.getHref() },
      { label: 'Logs', to: paths.enterprise.dashboard.settings.logs.getHref() },
    ],
  },
  {
    title: 'Support',
    items: [
      // { label: 'Login & security', to: paths.enterprise.dashboard.settings.security.getHref() },
      { label: 'Terms of use', to: paths.enterprise.dashboard.settings.terms.getHref() },
      { label: 'Privacy policy', to: paths.enterprise.dashboard.settings.privacy.getHref() },
    ],
  },
]

const EnterpriseSidebarNav = () => {
  const { data: userData } = useMyUserData()
  const { data: enterprise } = useGetEnterprise({ enterpriseId: userData?.enterpriseId ?? -1 })

  const companyName = enterprise?.name || 'Company'
  const companyTypeLabel =
    enterpriseCompanyTypeMap[enterprise?.companyType as EnterpriseCompanyTypeValues]?.label ||
    enterprise?.companyType ||
    'Enterprise'

  return (
    <div className="flex w-full flex-col gap-7">
      <h1 className="text-[26px] font-semibold leading-[1.2] tracking-[-0.26px] text-primary">Settings</h1>

      <div className="flex items-center gap-[14px]">
        <div className="flex size-[48px] shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-accent shadow-[0_0_12px_0_rgba(27,48,41,0.1)]">
          {enterprise?.iconPicture ? (
            <img src={enterprise.iconPicture} alt={companyName} className="size-full object-cover" />
          ) : (
            <BuildingFilled size={20} className="text-muted" />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-primary">{companyName}</p>
          <p className="truncate text-xs font-medium text-muted">{companyTypeLabel}</p>
        </div>
      </div>

      <div className="flex flex-col gap-7">
        {settingsSections.map((section) => (
          <div key={section.title} className="flex flex-col gap-2.5">
            <p className="text-sm font-semibold tracking-[-0.25px] text-muted">{section.title}</p>
            <div className="flex flex-col gap-[3px]">
              {section.items.map((item) =>
                item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-[12px] px-3.5 py-3 text-sm font-semibold text-primary transition-colors hover:bg-accent/60"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'rounded-[12px] px-3.5 py-3 text-sm font-semibold text-primary transition-colors',
                        isActive && 'bg-accent',
                        !isActive && 'hover:bg-accent/60',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ),
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default EnterpriseSidebarNav
